import { useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useHistory } from "react-router-dom";

import { useEnrollment } from "@ip-hooks/mfa";
import { publicPath, trackEvent } from "@ip-utils";
import { ButtonWithLoadingIcon } from "@ip-widgets/Tailwind/common/button";
import { InputOutlined } from "@ip-widgets/Tailwind/common/input";
import component from "@ip-widgets/Tailwind/mfa/styled-components";

export default function SMSEnrollment() {
  const phoneInputEl = useRef<HTMLInputElement>();
  const bindingCodeInputEl = useRef<HTMLInputElement>();

  const [canContinue, setCanContinue] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const history = useHistory();

  const { enrollWithSMS, confirmSMS } = useEnrollment();

  useEffect(() => {
    function setIsEnrollingToFalse(e: any) {
      trackEvent("MFA", "Cancel enrollment");
      localStorage.setItem("isEnrolling", "false");
    }

    window.addEventListener("beforeunload", setIsEnrollingToFalse);

    return () =>
      window.removeEventListener("beforeunload", setIsEnrollingToFalse);
  }, []);

  useEffect(() => {
    function setIsEnrollingToFalse(e: any) {
      trackEvent("MFA", "Cancel enrollment");
      localStorage.setItem("isEnrolling", "false");
    }

    window.addEventListener("popstate", setIsEnrollingToFalse);

    return () => window.removeEventListener("popstate", setIsEnrollingToFalse);
  }, []);

  const cancelEnrollment = () => {
    trackEvent("MFA", "Cancel enrollment");

    localStorage.setItem("isEnrolling", "false");

    history.push("/security");
  };

  const enroll = async () => {
    if (!phoneInputEl?.current?.value) {
      return;
    }

    const phone = phoneInputEl.current.value;

    trackEvent("MFA", "Confirm enrollment");

    setLoading(true);
    const res = await enrollWithSMS(phone);
    setLoading(false);

    if (res.status === 200) {
      setCanContinue(true);
      trackEvent("MFA", "Successful enrollment");
    } else {
      const { error_description } = await res.json();
      toast.error(error_description);
      setError(true);
      setCanContinue(false);
    }
  };

  const confirmEnrollment = async () => {
    if (!bindingCodeInputEl?.current?.value) {
      return;
    }
    const binding_code = bindingCodeInputEl.current.value;

    setLoading(true);
    const res = await confirmSMS(binding_code);
    setLoading(false);

    if (res.status === 200) {
      setSuccess(true);

      toast.success("Success");

      setTimeout(() => {
        toast.loading("Redirecting");
      }, 2000);

      setTimeout(() => {
        history.push(`/security`);
        toast.dismiss();
      }, 3000);
    } else {
      const { error_description } = await res.json();
      toast.error(error_description);
      setError(true);
    }
  };

  return (
    <component.SPage>
      <component.SContainer>
        <component.SHeaderContainer>
          <component.SImage src={publicPath("/media/iglu-white.svg")} />
        </component.SHeaderContainer>
        <component.SBodyContainer>
          <component.SHeader>Secure Your Account</component.SHeader>
          <component.SDescription>
            Enter your country code and phone number to which we can send a
            6-digit code. Then enter your recieved One-time Password.
          </component.SDescription>
          {canContinue ? (
            <SMSForm
              inputEl={bindingCodeInputEl}
              onClick={confirmEnrollment}
              loading={loading}
              success={success}
              error={error}
            />
          ) : (
            <PhoneNumberForm
              inputEl={phoneInputEl}
              onClick={enroll}
              loading={loading}
              success={success}
              error={error}
            />
          )}
          <div className="w-full">
            <ButtonWithLoadingIcon
              text="Cancel enrollment"
              size="lg"
              onClick={cancelEnrollment}
              style={{ width: "100%", marginTop: 24, background: "#DC2626" }}
            />
          </div>
        </component.SBodyContainer>
      </component.SContainer>
    </component.SPage>
  );
}

const PhoneNumberForm = ({
  onChange,
  inputEl,
  onClick,
  loading,
  success,
  error,
}: any) => {
  return (
    <div className="w-full mt-4">
      <InputOutlined
        id="phone-number-input"
        onChange={onChange}
        inputRef={inputEl}
        autoFocus
        placeholder="+1234567890"
      />
      <div className="w-full mt-4">
        <ButtonWithLoadingIcon
          text="Continue"
          size="full"
          loading={loading}
          success={success}
          error={error}
          onClick={onClick}
        />
      </div>
    </div>
  );
};

const SMSForm = ({
  loading,
  onChange,
  inputEl,
  onClick,
  success,
  error,
}: any) => {
  return (
    <div className="w-full mt-4">
      <InputOutlined
        id="sms-input"
        onChange={onChange}
        placeholder="XXXXXX"
        inputRef={inputEl}
        autoFocus
      />
      <div className="w-full mt-4">
        <ButtonWithLoadingIcon
          text="Enroll"
          size="full"
          loading={loading}
          success={success}
          error={error}
          onClick={onClick}
        />
      </div>
    </div>
  );
};
